import { Widget } from '../Widget';
import { WidgetWithScheduler } from '../WidgetWithScheduler';
import { HandlebarsTemplate, TemplateWidgetMixin } from '../../Template';
import { Log } from '../../AuxillaryService/Log';
import { ReachAPI } from '../../ReachApi';
import { Signage } from '../../Signage';
import { $, jQuery } from '../../../vendor/jquery';
import '../../../vendor/jquery/jquery.mouseSwipe';
import { moment } from '../../../vendor/moment';
import { objectHasOwnProperty } from '../../Utility';
import { getRRuleSetFromString } from '../../Utility/RRule';
import {
    ZodScheduleWidgetConfigurationDetails,
    ScheduleWidgetConfigurationDetails,
    ScheduleWidgetConfiguration,
    ScheduleInfo,
    Calendar,
    ScheduleWidgetTemplateRenderData,
    ScheduleEventInfo,
    ParsedEvents,
    ScheduleWidgetEvent,
    RawEvent,
    RawExceptionEvent,
    ScheduleExceptionEvent,
    CalendarTemplate,
    GetCalendarGroupsAPIResponse,
    GetCalendarAPIResponse,
    GetCalendarsAPIResponse,
    CalendarWithEvents,
    CalendarChangeKeyRecord,
    CalendarQuarter,
    RecurrenceEvent,
    ZodCalendarWithEvents,
    ScheduleWidgetDisplayEvent,
    newExceptionEvent,
    GetScheduleResponse,
} from './types';
import { TimelineSlot, WidgetType } from '../types';
import { Moment } from 'moment';
import { ScheduleEventRefresher } from './ScheduleEventRefresher';
import { isNullish, Nullish } from '../../Utility/Nullish';
import { pushReceiver } from '../../MessagingClient/instance';
import './schedule.scss';

class ScheduleWidget extends TemplateWidgetMixin(WidgetWithScheduler) {
    static DEFAULT_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

    readonly type: WidgetType.SCHEDULE = WidgetType.SCHEDULE;

    declare config: ScheduleWidgetConfigurationDetails;

    schedules: ScheduleInfo[];

    /**
     * The index of the next schedule to fade-in
     */
    fadeIdx: number;

    /**
     * The interval ID of the fade timer
     */
    fadeIntervalId: number | undefined;

    /**
     * The time, in milliseconds, the fade timer should delay in between
     * executions
     */
    fadeInterval: number; // 10 seconds

    /**
     * Date object to send to API
     */
    calendarDate: Moment;

    /**
     * Whether to allow scrolling via touch
     */
    allowScrollingViaTouch: boolean;

    /**
     * Whether to display an event's information when that event is
     * clicked/touched
     */
    displayEventInfoOnClick: boolean;

    /**
     * Whether to fade in additional schedules (when scrolling is
     * disabled)
     */
    fadeInAdditionalSchedules: boolean;

    /**
     * Whether to utilize calendar controls to change days
     */
    calendarControls: boolean;

    /**
     * The amount, in pixels, the mouse can move while clicked/touched and still
     * be considered a click
     */
    touchTolerance: number;

    /**
     * Whether the mouse is currently down/clicked
     */
    mouseIsDown: boolean;

    /**
     * The **X** position the mouse was at on the last `mouseup`/`touchend`
     * event
     */
    mouseEndX: number;

    /**
     * The **Y** position the mouse was at on the last `mouseup`/`touchend`
     * event
     */
    mouseEndY: number;

    /**
     * The **X** position the mouse was at on the last `mousedown`/`touchstart`
     * event
     */
    mouseStartX: number;

    /**
     * The **Y** position the mouse was at on the last `mousedown`/`touchstart`
     * event
     */
    mouseStartY: number;

    /**
     * The **X** position the mouse was at on the last `touchmove` event
     */
    mouseMoveX: number;

    /**
     * The **Y** position the mouse was at on the last `touchmove` event
     */
    mouseMoveY: number;

    /**
     * List of calendars in the widget
     */
    calendarChangeKeyRecords: CalendarChangeKeyRecord[];

    /**
     * Stores the schedule data object so that it does not need to be fetched
     * again on a basic refresh
     */
    schedulePromiseData: ScheduleInfo[] | undefined;

    /**
     * Helps execute the scheduleIntervalAction function every minute.
     *
     * Todo: replace with the widget scheduler
     */
    eventRefresher: ScheduleEventRefresher | undefined;

    /**
     * Stores a window.setTimeout id to help time out fullscreen videos on touchscreen schedules
     */
    fullScreenVideoTimeoutId: number | undefined;

    /**
     * Boolean to set when the schedule marquee is scrolling
     * @type {boolean}
     */
    isScrolling = false;

    constructor(widgetConfiguration: ScheduleWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        this.schedules = [];

        this.fadeIdx = 0;

        this.fadeInterval = 10 * 1000; // 10 seconds

        this.calendarDate = moment();

        this.allowScrollingViaTouch = false;

        this.displayEventInfoOnClick = false;

        this.fadeInAdditionalSchedules = false;

        this.calendarControls = false;

        this.touchTolerance = 5;

        this.mouseIsDown = false;

        this.mouseEndX = 0;

        this.mouseEndY = 0;

        this.mouseStartX = 0;

        this.mouseStartY = 0;

        this.mouseMoveX = 0;

        this.mouseMoveY = 0;

        this.calendarChangeKeyRecords = [];

        // The below code is needed to bind `this` to the function(s), but still be
        // able to add and remove the function as an event listener.
        // (http://stackoverflow.com/a/33386309)
        //todo: determine if these are bound correctly, coherently
        this.handleDisplayEventInfoEvent = this.handleDisplayEventInfoEvent.bind(this);
        this.closeSingleEvent = this.closeSingleEvent.bind(this);
        this.enableFullscreenVideo = this.enableFullscreenVideo.bind(this);
        this.disableFullscreenVideo = this.disableFullscreenVideo.bind(this);
    }

    /**
     * Not entirely sure how this works. However, a key thing to note here is that
     * we are creating a textarea element. Using anything else will strip the `<br>`
     * tags from the string, formatting the string incorrectly.
     */
    static getHtmlTextContent(html: string): string {
        const decoder: HTMLTextAreaElement = document.createElement('textarea');
        decoder.innerHTML = html;
        return decoder.textContent as string;
    }
    /**
     * Strip HTML elements from event data
     */
    static htmlDecode(scheduleInfoArray: ScheduleInfo[]): ScheduleInfo[] {
        return scheduleInfoArray.map((scheduleInfo) => {
            return {
                headerinfo: {
                    ...scheduleInfo.headerinfo,
                    rinkname: !isNullish(scheduleInfo.headerinfo.rinkname)
                        ? ScheduleWidget.getHtmlTextContent(scheduleInfo.headerinfo.rinkname)
                        : '',
                    mainheadertext: !isNullish(scheduleInfo.headerinfo.mainheadertext)
                        ? ScheduleWidget.getHtmlTextContent(scheduleInfo.headerinfo.mainheadertext)
                        : '',
                },
                //TODO: This was originally not treating scheduleInfo.events as an array
                // but rather an object.  Determine how it should actually be handled
                events: scheduleInfo.events.map((event) => {
                    return {
                        ...event,
                        event: !isNullish(event.event)
                            ? ScheduleWidget.getHtmlTextContent(event.event)
                            : '',
                        hometeam: !isNullish(event.hometeam)
                            ? ScheduleWidget.getHtmlTextContent(event.hometeam)
                            : '',
                        awayteam: !isNullish(event.awayteam)
                            ? ScheduleWidget.getHtmlTextContent(event.awayteam)
                            : '',
                        awaylocker: !isNullish(event.awaylocker)
                            ? ScheduleWidget.getHtmlTextContent(event.awaylocker)
                            : '',
                        homelocker: !isNullish(event.homelocker)
                            ? ScheduleWidget.getHtmlTextContent(event.homelocker)
                            : '',
                        information: !isNullish(event.information)
                            ? ScheduleWidget.getHtmlTextContent(event.information)
                            : '',
                        location: !isNullish(event.location)
                            ? ScheduleWidget.getHtmlTextContent(event.location)
                            : '',
                        rinkname: !isNullish(event.rinkname)
                            ? ScheduleWidget.getHtmlTextContent(event.rinkname)
                            : '',
                        mainheadertext: !isNullish(event.mainheadertext)
                            ? ScheduleWidget.getHtmlTextContent(event.mainheadertext)
                            : '',
                    };
                }),
            };
        });
    }

    /**
     * Get the total number of events in the schedule(s)
     */
    static determineTotalEvents(schedules: ScheduleInfo[]): number {
        let totalEvents = 0;
        for (let x = 0; x < schedules.length; x++) {
            totalEvents += schedules[x].events.length;
        }

        return totalEvents;
    }

    protected validateConfigurationDetails(
        maybeScheduleWidgetConfigurationDetails: ScheduleWidgetConfigurationDetails
    ): ScheduleWidgetConfigurationDetails {
        return ZodScheduleWidgetConfigurationDetails.parse(maybeScheduleWidgetConfigurationDetails);
    }

    /**
     * Initialize Widget
     * @listens MessagingClient#calendar-update
     */
    async initialize(): Promise<void> {
        this.allowScrollingViaTouch = this.config.touch_scroll ?? false;

        this.displayEventInfoOnClick = this.config.event_click ?? false;
        this.fadeInAdditionalSchedules = this.config.fade ?? false;

        this.calendarControls = this.config.calendar_controls ?? false;

        this.initEventRefresher();

        // Real-time updates!
        pushReceiver().addListener('calendar-update', (event) => {
            if (
                this.config.calendar_id === event.detail.calendarId ||
                this.calendarChangeKeyRecords.some(
                    (calendar) => calendar.calendar_id === event.detail.calendarId
                )
            ) {
                Log.Info(
                    `Received push notification from MessagingClient for calendar ${event.detail.calendarId}`,
                    null,
                    __fileline
                );

                this.getScheduleInfo();
            }
        });

        return super.initialize();
    }

    async _doPause(): Promise<void> {
        this.pauseTimer();
    }

    async play(currentSlot: TimelineSlot): Promise<void> {
        /*
         * When play is triggered, startTimer function will trigger the schedulerIntervalAction
         * super.play should be at the top because schedulerIntervalAction will exit
         * without doing anything if the widget state is not set to playing.
         */
        await super.play(currentSlot);

        const elem: HTMLMarqueeElement | null = this.wrapperElement.querySelector(
            `#calmarquee_${this.id}`
        );
        if (elem && !this.isScrolling) {
            elem.start();
            this.isScrolling = true;
        }

        if (this.config.calendar_id || this.config.group_id) {
            this.eventRefresher?.startTimer();
        }

        return Promise.resolve();
    }

    async getReadyForDisplay(): Promise<void> {
        // call the scheduler explicitly and tell it to draw the last
        // retrieved calendar onto the DOM
        await this.getScheduleInfo(true);
    }

    async stop(): Promise<void> {
        await super.stop();
        this.pauseTimer();
    }

    //viewBag is used by handlebars templates, but not vue templates
    protected getRenderContext(): ScheduleWidgetTemplateRenderData {
        return {
            schedule: this.schedules,
            wobj: {
                widget: {
                    widgetsid: this.id,
                },
            },
            widget: { id: this.id },
            Widget: { Id: this.id },
            viewBag: {
                widgetid: this.id,
                widget_css_header: this.config.css_header,
                widget_css_labels: this.config.css_labels,
                widget_css_events: this.config.css_events,
                widget_css_times: this.config.css_time,
                pixels_per_second: this.config.scroll_speed
                    ? parseInt(this.config.scroll_speed) * 15
                    : 15,
            },
            metadata: {
                //add metadata as needed - extra properties or template specific
                //properties can go here
                marquee: {
                    active: true,
                    speed: this.config.scroll_speed || '2',
                    override: this.config.scroll_override ?? false,
                    touchScroll: this.config.touch_scroll ?? false,
                    allowTouchScrolling: this.allowScrollingViaTouch,
                    copy: [1], //minimum number of copies required for continuous marquee effect
                },
            },
        };
    }

    /**
     * Render content to the DOM
     * @todo: See if there is an alternative to passing the render context directly
     *   to renderContent
     */
    async renderContentWithGlobals(schedules?: ScheduleInfo[]): Promise<void> {
        const renderContext = this.getRenderContext();

        if (schedules) {
            renderContext.schedule = schedules;
        }

        await this.renderContent(renderContext);
    }

    initEventRefresher(): void {
        const schedulerIntervalActionBound = scheduleIntervalAction.bind(this);
        this.eventRefresher = new ScheduleEventRefresher(schedulerIntervalActionBound);
    }

    schedulerAction(): void {
        this.getScheduleInfo();
    }

    /**
     * Make an API call to obtain schedule data.  This is also the schedulerAction
     * @param forceRedrawCalendar forces the redraw of the calendar if true
     */
    async getScheduleInfo(forceRedrawCalendar = false): Promise<void> {
        let getScheduleJqPromise: Promise<ScheduleInfo[]> | undefined;
        const currentDate = moment();

        const timezone = this.getTimezone();

        const calendarDateOnlyString = this.calendarDate.toDate().toLocaleDateString();

        //disable calendar controls
        if (this.calendarControls) {
            this.wrapperElement
                .querySelector(`#next-day-btn-${this.id}`)
                ?.removeEventListener('click', this.calendarControlsAddDay);

            this.wrapperElement
                .querySelector(`#prev-day-btn-${this.id}`)
                ?.removeEventListener('click', this.calendarControlsSubtractDay);
        }

        if (forceRedrawCalendar && this.schedulePromiseData !== undefined) {
            getScheduleJqPromise = Promise.resolve(this.schedulePromiseData);
        } else if (this.config.screenid) {
            getScheduleJqPromise = ReachAPI.signage
                .getSchedule(
                    this.config.facilityid,
                    this.config.screenid,
                    this.config.format,
                    this.config.additional_days,
                    currentDate.format('M/D/YYYY h:mm:ss A'),
                    timezone,
                    this.calendarControls
                        ? 'true'
                        : this.config.show_expired_data
                        ? 'true'
                        : 'false',
                    this.calendarControls ? calendarDateOnlyString : null
                )
                .then((response: GetScheduleResponse) => response.schedules);
        } else if (this.config.group_id) {
            getScheduleJqPromise = this.getGroupSchedules(
                currentDate,
                this.config.additional_days,
                timezone
            );
        } else if (this.config.calendar_id) {
            // Calendar List Acquired!
            this.subscribeToCalendars([this.config.calendar_id]);

            getScheduleJqPromise = this.getCalendarSchedule(
                currentDate,
                this.config.additional_days,
                timezone
            );
        }

        if (getScheduleJqPromise) {
            try {
                const scheduleInfoData: ScheduleInfo[] = await getScheduleJqPromise;
                return this.processScheduleInfo(scheduleInfoData);
            } catch (error) {
                Log.Warning(
                    `Failed to get Schedule for Widget:${this.id}`,
                    null,
                    __fileline,
                    error
                );
            }
        }

        if (this.config.calendar_id || this.config.group_id) {
            this.eventRefresher?.startTimer();
        }
    }

    /**
     * This function is ran after we decided which schedule promise we will use. It
     * will then get the resulting schedule from that promise and process it
     * properly
     * - this promise returns the result of the fetched schedule
     */
    async processScheduleInfo(scheduleInfoArray: ScheduleInfo[]): Promise<void> {
        this.schedulePromiseData = scheduleInfoArray;

        this.schedules = ScheduleWidget.htmlDecode(scheduleInfoArray);
        this.fadeIdx = 0;

        const totalEvents = ScheduleWidget.determineTotalEvents(this.schedules);

        this.hasContentToPlay = totalEvents !== 0;

        try {
            await this.renderContentWithGlobals();
        } catch (error) {
            Log.Error(
                `Failed to render widget content (${this.id}:${this.name})`,
                null,
                __fileline,
                error
            );
        }

        //UI touch and marquee setup
        this.setupTouchUI();
    }

    /**
     * Ensures the schedule is ready to receive touch events
     *
     * @private
     */
    async setupTouchUI(): Promise<void> {
        if (this.displayEventInfoOnClick) {
            // add mouse/touch events
            //todo: Implement better event handler management
            // Unsure why we need to remove the handler only to re-add it
            const eventRowElements = this.wrapperElement.querySelectorAll('.eventrow');
            Array.from(eventRowElements).forEach((eventRowElement) => {
                eventRowElement.removeEventListener('mousedown', this.handleDisplayEventInfoEvent);
                eventRowElement.removeEventListener('mouseup', this.handleDisplayEventInfoEvent);
                eventRowElement.removeEventListener('mousemove', this.handleDisplayEventInfoEvent);
                eventRowElement.addEventListener('mousedown', this.handleDisplayEventInfoEvent);
                eventRowElement.addEventListener('mouseup', this.handleDisplayEventInfoEvent);
                eventRowElement.addEventListener('mousemove', this.handleDisplayEventInfoEvent);

                if (this.allowScrollingViaTouch) {
                    eventRowElement.removeEventListener(
                        'touchstart',
                        this.handleDisplayEventInfoEvent
                    );
                    eventRowElement.removeEventListener(
                        'touchend',
                        this.handleDisplayEventInfoEvent
                    );
                    eventRowElement.removeEventListener(
                        'touchmove',
                        this.handleDisplayEventInfoEvent
                    );
                    eventRowElement.addEventListener(
                        'touchstart',
                        this.handleDisplayEventInfoEvent
                    );
                    eventRowElement.addEventListener('touchend', this.handleDisplayEventInfoEvent);
                    eventRowElement.addEventListener('touchmove', this.handleDisplayEventInfoEvent);
                }
            });
        }

        if (this.allowScrollingViaTouch) {
            // add touch controls
            this.determineTouchScrolling();
        } else {
            //no touch scroll
            const doMarquee =
                !this.displayEventInfoOnClick &&
                !this.config.scroll_override &&
                !this.fadeInAdditionalSchedules &&
                !this.calendarControls;
            //if all event click, scroll override, fade and calendar
            //controls are turned off, build marquee

            // determine display
            if (doMarquee) {
                //VueScheduleTemplate has its own logic for marquee.
                if (this.template instanceof HandlebarsTemplate) {
                    this.determineMarquee();
                }
            } else {
                if (this.fadeInAdditionalSchedules) {
                    await this.createSchedulesForOverflowEvents();
                    // fade to overflow events
                    this.startFadeInAdditionalSchedules();
                } else if (this.config.scroll_override) {
                    await this.renderContentWithGlobals();
                    //need to remove events that are overflowing
                    //the page since scroll and fade are disabled
                    await this.removeOverflowEvents();
                }
            }
        }

        //calendar controls for tablet functionality
        if (this.calendarControls) {
            this.addCalendarControls();
        }
    }

    /**
     * Gets events in a group
     * @todo: getGroupSchedules and getCalendarSchedulers are ~95% similar, refactor to reuse logic
     */
    async getGroupSchedules(
        startDate: Moment | undefined = undefined,
        additionalDays: number | Nullish,
        timezone: string
    ): Promise<ScheduleInfo[]> {
        const getCalendarPromise: Promise<GetCalendarGroupsAPIResponse> =
            ReachAPI.signage.getCalendarGroups(this.config.facilityid, this.config.group_id);

        //todo: determine type
        const data = await getCalendarPromise;

        //todo: verifying change_key might be redundant
        //todo: instead of partial calendarList we might have to send full
        // calendarlist because only events from api is used for rendered
        // rendered events from other calendars might be discarded

        // Calendar List Acquired!
        this.subscribeToCalendars(data.groups.calendars.map((calendar) => calendar.calendar_id));

        const calendarList = [];
        data.groups.calendars.forEach((calendar) => {
            //Check if each calendar is present in schedule calendars list
            const matchedCalendar = this.calendarChangeKeyRecords.find((item) => {
                return calendar.calendar_id === item.calendar_id;
            });

            if (matchedCalendar) {
                //if yes check to see if change_key is matching
                if (
                    (startDate && startDate.isAfter(this.calendarDate, 'day')) ||
                    matchedCalendar.change_key !== calendar.change_key
                ) {
                    //if not get calendar schedule
                    calendarList.push(calendar.calendar_id);
                }
            } else {
                //if not get calendar schedule
                calendarList.push(calendar.calendar_id);
            }
        });

        if (!calendarList.length) {
            throw new Error('No calendars require retrieval!'); //Todo, use a better message
        }

        const calendarListToQuery = data.groups.calendars.map(function (item) {
            return item.calendar_id;
        });

        const sdate = moment(startDate).tz(timezone);
        const edate = moment(startDate || undefined)
            .add(additionalDays, 'days')
            .tz(timezone)
            .endOf('day');

        this.calendarDate = moment();

        const calendarsWithEvents = await this.getCalendarData(
            sdate,
            additionalDays,
            calendarListToQuery
        );

        return this.parseCalendars(
            calendarsWithEvents,
            sdate.format(ScheduleWidget.DEFAULT_DATETIME_FORMAT),
            edate.format(ScheduleWidget.DEFAULT_DATETIME_FORMAT),
            additionalDays
        );
    }

    /**
     * Gets events in a calendar
     * @todo: getGroupSchedules and getCalendarSchedulers are ~95% similar, refactor to reuse logic
     */
    async getCalendarSchedule(
        startDate: Moment | undefined = undefined,
        additionalDays: number | Nullish,
        timezone: string
    ): Promise<ScheduleInfo[]> {
        const getCalendarPromise: Promise<GetCalendarAPIResponse> = ReachAPI.signage.getCalendar(
            this.config.facilityid,
            this.config.calendar_id
        );

        //get calendar info
        //todo: should this calendar use `subscribeToCalendars`?
        const data = await getCalendarPromise;

        //find calendar
        const calendar = this.calendarChangeKeyRecords.find((item) => {
            return item.calendar_id === this.config.calendar_id;
        });

        let newCalendarId: string;
        //if found
        if (calendar) {
            //if yes check if change_key is matching
            //  match - do not update schedule
            //  not match - update schedule
            if (
                (startDate && startDate.isAfter(this.calendarDate, 'day')) ||
                calendar.change_key !== data.calendar.change_key
            ) {
                //if not get calendar schedule
                newCalendarId = calendar.calendar_id;
            } else {
                /**
                 * TODO: this is not an error condition
                 * this means nothing in database has changed yet.
                 * Rather than throwing error this should be handled gracefully,
                 * basically nothing needs to be done, i.e. display available data
                 */
                throw new Error("We haven't loaded anything from the database at this point");
            }
        } else {
            newCalendarId = data.calendar.calendar_id;
        }

        const sdate = moment(startDate).tz(timezone);
        const edate = moment(startDate).add(additionalDays, 'days').tz(timezone).endOf('day');

        const calendarsWithEvents = await this.getCalendarData(sdate, additionalDays, [
            newCalendarId,
        ]);

        this.calendarDate = moment();

        return this.parseCalendars(
            calendarsWithEvents,
            sdate.format(ScheduleWidget.DEFAULT_DATETIME_FORMAT),
            edate.format(ScheduleWidget.DEFAULT_DATETIME_FORMAT),
            additionalDays
        );
    }

    async getCalendarData(
        startDate: Moment,
        additionalDays: number | Nullish,
        calendarIds: string[] = []
    ): Promise<CalendarWithEvents[]> {
        return new Promise((resolve, reject) => {
            //Get calendar schedule
            ReachAPI.signage
                .getCalendars(
                    this.config.facilityid,
                    null, //no group id
                    calendarIds,
                    moment(startDate).utc().format(ScheduleWidget.DEFAULT_DATETIME_FORMAT),
                    moment(startDate)
                        .add(additionalDays, 'days')
                        .utc()
                        .format(ScheduleWidget.DEFAULT_DATETIME_FORMAT)
                )
                .done((response: GetCalendarsAPIResponse) => {
                    /**
                     * An array of tuples for error logging.  The first value is the object that
                     * failed validation.  The second is an instance of a ZodError that contains
                     * details about what specifically failed during validation.
                     */
                    const calendarsWithEventsValidationErrors: [unknown, unknown][] = [];

                    const calendarsWithEvents = response.calendars.map(
                        (maybeCalendarWithEvents) => {
                            try {
                                return ZodCalendarWithEvents.parse(maybeCalendarWithEvents);
                            } catch (error) {
                                calendarsWithEventsValidationErrors.push([
                                    maybeCalendarWithEvents,
                                    error,
                                ]);

                                return maybeCalendarWithEvents;
                            }
                        }
                    );

                    if (calendarsWithEventsValidationErrors.length > 0) {
                        Log.Warning(
                            `Data for calendar widget (id=${this.id}), has ${calendarsWithEventsValidationErrors.length} validation errors.`,
                            null,
                            __fileline
                        );
                    }

                    //return events in the range
                    resolve(calendarsWithEvents);
                })
                .fail(function (err: unknown) {
                    reject(err);
                });
        });
    }

    /**
     * pauses the timer on the marquee element
     */
    pauseTimer(): void {
        const elem: HTMLMarqueeElement | null = this.wrapperElement.querySelector(
            `#calmarquee_${this.id}`
        );
        if (elem && this.isScrolling) {
            elem.stop();
            this.isScrolling = false;
        }

        if (this.config.calendar_id || this.config.group_id) {
            this.eventRefresher?.stopTimer();
        }
    }

    /**
     * Get the event information for a specific event
     */
    getEventInfo(
        eventInfoId: string | number,
        scheduleRinkId: string | number
    ): ScheduleEventInfo | null {
        let rinkIdx = null;
        let eventIdx = null;

        // find the schedule index
        for (let idx = 0; idx < this.schedules.length; idx++) {
            if (this.schedules[idx].headerinfo.rinkid == scheduleRinkId) {
                rinkIdx = idx;
            }
        }

        // find the event index
        if (null !== rinkIdx) {
            for (let idx = 0; idx < this.schedules[rinkIdx].events.length; idx++) {
                if (this.schedules[rinkIdx].events[idx].infoid == eventInfoId) {
                    eventIdx = idx;
                }
            }
        }

        if (eventIdx !== null && rinkIdx !== null) {
            return this.schedules[rinkIdx].events[eventIdx];
        } else {
            Log.Warning(
                `No event info found for eventInfoId: ${eventInfoId}, scheduleRinkId: ${scheduleRinkId}`,
                null,
                __fileline
            );
            return null;
        }
    }

    /**
     * Appends calendar controls HTML to widget
     * @todo: Remove JQuery
     * @todo: See if we can offload monkeying with the calendar templates, can we assign
     *   this work to the template engine?
     */
    addCalendarControls(): void {
        const $widget = $(this.wrapperElement);

        const isCurrentDate = this.calendarDate.isSame(moment(), 'day');

        const dayText = isCurrentDate ? 'TODAY' : this.calendarDate.format('dddd, LL');

        const topOffset = ($widget.outerHeight() || 0) + parseInt($widget.parent().css('top'));

        const calendarControlsHtml = `
            <div class="calendar-controls-container calendar-controls-${this.id}"
                id="calendar_controls_${this.id}"
                style="top:${topOffset}px;width:${$widget.outerWidth()}px;"
            >
                <div
                    id="prev-day-btn-${this.id}"
                    class="calendar-controls-btn-left calendar-controls-previous-${this.id}"
                >
                    &#9664; PREVIOUS DAY
                </div>
                <div
                    id="calendar-controls-display-date-${this.id}"
                    class="calendar-controls-date-display calendar-controls-date-display-${this.id}"
                >
                    ${dayText}
                </div>
                <div
                    id="next-day-btn-${this.id}"
                    class="calendar-controls-btn-right calendar-controls-next-${this.id}"
                >
                    NEXT DAY &#9654;
                </div>
            </div>`;

        $widget.find(`#calendartable_${this.id}`).before(calendarControlsHtml);

        $widget.find(`#next-day-btn-${this.id}`).bind('click', this.calendarControlsAddDay);

        $widget.find(`#prev-day-btn-${this.id}`).bind('click', () => {
            this.calendarControlsSubtractDay();
        });
    }

    /**
     * Adds a day to calendarDate
     */
    calendarControlsAddDay = (): void => {
        this.calendarDate.add(1, 'day');
        this.getScheduleInfo();
    };

    /**
     * Subtracts a day to calendarDate
     */
    calendarControlsSubtractDay = (): void => {
        this.calendarDate.subtract(1, 'day');
        this.getScheduleInfo();
    };

    /**
     * Display event information in place of the schedule information
     */
    displaySingleEvent(eventInfo: ScheduleEventInfo): void {
        const wid = this.id;
        const $widget = $(this.wrapperElement);

        $widget.find(`#calendartable_${wid}`).stop();

        $widget.find(`#touch-event-back-${wid}`).off('click', this.closeSingleEvent);

        $widget
            .find(`#touch-event-title-${wid}`)
            .html(eventInfo.information ? eventInfo.information : '');

        $widget
            .find(`#touch-event-info-startdate-${wid}`)
            .html(eventInfo.sdate ? eventInfo.sdate + ' ' : '');
        $widget
            .find(`#touch-event-info-starttime-${wid}`)
            .html(eventInfo.stime ? eventInfo.stime + ' - ' : '');
        $widget
            .find(`#touch-event-info-enddate-${wid}`)
            .html(eventInfo.edate ? eventInfo.edate + ' ' : '');
        $widget
            .find(`#touch-event-info-endtime-${wid}`)
            .html(eventInfo.etime ? eventInfo.etime + '<br />' : '');
        $widget
            .find(`#touch-event-info-location-${wid}`)
            .html(eventInfo.information ? String(eventInfo.location) + '<br />' : '');
        $widget
            .find(`#touch-event-info-instructor-${wid}`)
            .html(eventInfo.instructor ? eventInfo.instructor : '');

        //uses regular expression to remove video url from description
        $widget
            .find(`#touch-event-desc-${wid}`)
            .html(eventInfo.descr ? eventInfo.descr.replace(/\[[^\]]*\]/, '') : '');

        $widget.find(`#eventdiv_${wid}`).show();
        $widget.find(`#calendartable_${wid}`).hide();

        $widget.find(`#touch-event-back-${wid}`).on('click', this.closeSingleEvent);

        const videoSource = this.determineVideoSource(eventInfo);
        if (videoSource) {
            const $video = $widget.find<HTMLVideoElement>(`#touch-event-video-${wid}`);
            $video.find('source').attr('src', videoSource);

            $video.get(0).load();
            $video.on('click', this.enableFullscreenVideo);
        }
    }

    /**
     * Hide the event information and re-display the schedule information
     */
    closeSingleEvent(): void {
        const $widget = $(this.wrapperElement);
        const $video = $widget.find(`#touch-event-video-${this.id}`);

        $widget.find(`#eventdiv_${this.id}`).hide();
        if ($video.length > 0) {
            $video.find('source').attr('src', '');
            ($video.get(0) as HTMLVideoElement).load();
            $video.off();
        }
        $widget.find(`#calendartable_${this.id}`).show();
    }

    /**
     * Find video source from event info
     * @todo: refactor to pass description in directly
     * @todo: refactor to return string | null
     */
    determineVideoSource(eventInfo: ScheduleEventInfo): string | undefined {
        /**
         * Video URLs are stored in the description in this format:
         *
         * 'Test description. Blah blah blah.[video:https://www.test.com/video]'
         */
        const matches = eventInfo.descr?.match(/\[video:(.*?)\]/);
        return Array.isArray(matches) ? matches[1] : undefined;
    }

    enableFullscreenVideo(): void {
        const $widget = $(this.wrapperElement);
        const $video = $widget.find(`#touch-event-video-${this.id}`);

        //add override zone css for full screen
        $video.css({
            position: 'absolute',
            top: '0',
            left: '0',
            width: '100%',
            height: '100%',
            'z-index': '1000',
            'background-color': 'black',
        });

        //we must set the position of the zone to static, otherwise the fullscreen
        //video will only fill out the zone
        $widget.css('position', 'static');
        $widget.parent().css('position', 'static');

        $video.on('click', this.disableFullscreenVideo);
        $video.off('click', this.enableFullscreenVideo);
        //add timeout to full screen video (30 seconds)
        this.fullScreenVideoTimeoutId = window.setTimeout(this.disableFullscreenVideo, 30000);
    }

    disableFullscreenVideo(): void {
        clearTimeout(this.fullScreenVideoTimeoutId);

        const $widget = $(this.wrapperElement);
        const $video = $widget.find(`#touch-event-video-${this.id}`);

        //remove fullscreen css
        $video.css({
            position: '',
            top: '',
            left: '',
            width: '',
            height: '',
            'z-index': '',
            'background-color': '',
        });

        $widget.css('position', '');
        $widget.parent().css('position', '');

        $video.on('click', this.enableFullscreenVideo);
        $video.off('click', this.disableFullscreenVideo);
    }

    /**
     * Modify the schedule information to support touch scrolling
     */
    determineTouchScrolling(): void {
        const $widget = $(this.wrapperElement);
        const $calendarTableOrEventDiv = $widget.find(
            `#calendartable_${this.id},#eventdiv_${this.id}`
        );
        const $calendarTable = $widget.find(`#calendartable_${this.id}`);
        const $calendarTableTable = $calendarTable.find('table');

        // JavaScript Document
        $widget.css('overflow', 'hidden');

        $calendarTableOrEventDiv.css('-webkit-user-select', 'none');
        $calendarTableOrEventDiv.css('-moz-user-select', 'none');
        $calendarTableOrEventDiv.css('-webkit-touch-callout', 'none');
        $calendarTableOrEventDiv.css('-khtml-user-select', 'none');
        $calendarTableOrEventDiv.css('-ms-user-select', 'none');
        $calendarTableOrEventDiv.css('user-select', 'none');

        $calendarTable.css('left', '0px');
        $calendarTable.css('top', '0px');
        $calendarTable.css('position', 'relative');
        $calendarTable.css('margin', '0px');
        $calendarTable.css('cursor', 'move');

        let calendarTableHeight = $calendarTableTable.height();
        const widgetHeight = $widget.outerHeight();

        if (calendarTableHeight && widgetHeight && calendarTableHeight < widgetHeight) {
            calendarTableHeight = widgetHeight;
        }

        $calendarTableTable.wrap(
            `<div class='panel' style='height:${calendarTableHeight}px;'></div>`
        );

        $calendarTable.swipe({
            TYPE: 'mouseSwipe',
            HORIZ: false,
            SNAPDISTANCE: 20,
            DURATION: 300,
            EASING: 'swing',
        });
    }

    /**
     * Modify the schedule information to continuously scroll events
     */
    determineMarquee(): void {
        const $widget = $(this.wrapperElement);
        const $calendarTable = $widget.find(`#calendartable_${this.id}`);

        //move schedule object back into place
        const scrollSpeed = Widget.valueNotEmpty(this.config.scroll_speed)
            ? this.config.scroll_speed
            : '2';
        $widget.css('top', `${this.config.x}px`);

        const marqueeCreated = !!this.wrapperElement.querySelector(`#calmarquee_${this.id}`);
        const calendarTableHeight = $calendarTable.outerHeight();
        const widgetHeight = $widget.height();
        if (
            calendarTableHeight &&
            widgetHeight &&
            calendarTableHeight > widgetHeight &&
            !marqueeCreated
        ) {
            const html = $widget.html();
            const elem = $<HTMLMarqueeElement>(`
                <marquee
                    id='calmarquee_${this.id}'
                    height='100%'
                    width='100%'
                    direction='up'
                    scrolldelay='5'
                    behavior='scroll'
                    scrollamount='${scrollSpeed}'
                >
                    ${html}
                    ${html}
                    ${html}
                    ${html}
                    ${html}
                    ${html}
                    ${html}
                </marquee>
            `);

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            $widget.html(elem);

            // make sure to start the marque back up again if re are re-creating it
            // while it should be playing.
            if (!this.isPaused) {
                if (elem && !this.isScrolling) {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    elem.start(); //todo: cast to HTMLMarqueeElement
                    this.isScrolling = true;
                }
            }
        }
    }

    /**
     * Split the overflowed events into a separate schedules for the fader to
     * handle pagination
     */
    async createSchedulesForOverflowEvents(): Promise<void> {
        const schedules = [];
        for (const schedule of this.schedules) {
            await this.renderContentWithGlobals([schedule]);
            const eventsCopy = [...schedule.events];
            const headerinfoCopy = { ...schedule.headerinfo };
            const pageHeight = this.wrapperElement.offsetHeight;
            const calendarTable = document.getElementById(`calendartable_${this.id}`);
            if (calendarTable !== null) {
                const totalPages = Math.min(
                    Math.ceil(calendarTable.offsetHeight / pageHeight),
                    eventsCopy.length
                );
                const eventsPerPage = Math.ceil(eventsCopy.length / totalPages);

                for (let i = 0; i < Math.max(totalPages, 1); i++) {
                    const events = eventsCopy.splice(0, eventsPerPage);
                    schedules.push({ events: events, headerinfo: headerinfoCopy });
                }
            }
        }
        //Copy because of typescript, can probably type schedules manually
        const schedulesCopy = [...schedules];
        /*
         * This ensures that any schedules we split up to multiple
         * will get removed once their events have expired.
         * It will also ensure that at least 1 of any particular calendar still exists
         * in the array.
         */
        this.schedules = schedules.filter(
            (schedule) =>
                !(
                    schedule.events.length === 0 &&
                    schedulesCopy.find(
                        (inner) =>
                            inner !== schedule &&
                            inner.headerinfo.calendar_id === schedule.headerinfo.calendar_id
                    )
                )
        );
    }

    /**
     * Remove events that are overflowing, used when this.fadeInAdditionalSchedules is false
     * and this.config.scroll_override is true
     */
    async removeOverflowEvents(): Promise<void> {
        const calendarTable = document.getElementById(`calendartable_${this.id}`);
        const wrapperHeight = this.wrapperElement.offsetHeight;
        //remove children until table doesn't overflow
        if (calendarTable !== null) {
            while (calendarTable.offsetHeight > wrapperHeight) {
                const lastRow = calendarTable.querySelector('tr:last-child');
                if (lastRow !== null) {
                    lastRow.remove();
                } else {
                    break;
                }
            }
        }
    }

    /**
     * Start the process to fade-in additional schedules
     */
    async startFadeInAdditionalSchedules(): Promise<void> {
        if (this.fadeIntervalId) {
            clearInterval(this.fadeIntervalId);
        }

        $(this.wrapperElement).css('visibility', 'visible');

        await this.handleFadeInAdditionalSchedules();

        //only start fade interval if there is more than 1 schedule
        if (this.schedules.length > 1) {
            this.fadeIntervalId = window.setInterval(async () => {
                await this.handleFadeInAdditionalSchedules();
            }, this.fadeInterval);
        }
    }

    /**
     * Fade-in the next schedule not currently displayed
     */
    async handleFadeInAdditionalSchedules(): Promise<void> {
        if (this.fadeIdx >= this.schedules.length) {
            this.fadeIdx = 0;
        }

        const schedules = [this.schedules[this.fadeIdx]];
        // no animation on the fade out of the schedule section that we are leaving,
        // but we will fade into the upcoming schedule section (500ms)
        $(this.wrapperElement).hide();

        await this.renderContentWithGlobals(schedules);

        this.fadeIdx++;

        $(this.wrapperElement).fadeIn(500);
    }

    /**
     * Handle mouse/touch events used to determine if specific event information
     * should be shown
     */
    handleDisplayEventInfoEvent(event: Event): void {
        if (event instanceof MouseEvent) {
            return this.handleMouseEvent(event);
        } else if (event instanceof TouchEvent) {
            return this.handleTouchEvent(event);
        }

        Log.Warning(`No event handlers for event type ${event.type}`, null, __fileline, event);
    }

    /**
     * Hanlde mouse events used to select specific schedule events
     *
     * @param event
     * @private
     */
    private handleMouseEvent(event: MouseEvent): void {
        switch (event.type.toLowerCase()) {
            case 'mousedown':
                event.preventDefault();
                this.mouseIsDown = true;
                this.mouseStartX = event.pageX;
                this.mouseStartY = event.pageY;

                break;
            case 'mousemove':
                if (this.mouseIsDown) {
                    event.preventDefault();
                }

                break;
            case 'mouseup':
                event.preventDefault();
                this.mouseIsDown = false;
                this.mouseEndX = event.pageX;
                this.mouseEndY = event.pageY;

                if (
                    event.currentTarget &&
                    Math.abs(this.mouseStartX - this.mouseEndX) < this.touchTolerance &&
                    Math.abs(this.mouseStartY - this.mouseEndY) < this.touchTolerance
                ) {
                    const targetInfoId: string | undefined = $(event.currentTarget).attr(
                        'data-info-id'
                    );
                    const targetRinkId: string | undefined = $(event.currentTarget).attr(
                        'data-rink-id'
                    );

                    if (!targetInfoId || !targetRinkId) {
                        const errorMessage = 'Unable to extract event data from MouseEvent target';
                        Log.Warning(errorMessage, null, __fileline, {
                            widgetId: this.id,
                            event: event,
                            eventData: {
                                targetInfoId: targetInfoId,
                                targetRinkId: targetRinkId,
                            },
                        });
                        return;
                    }

                    const eventClicked = this.getEventInfo(targetInfoId, targetRinkId);
                    if (eventClicked) {
                        this.displaySingleEvent(eventClicked);
                    }
                }

                break;
        }
    }

    /**
     * Handle touch events used to select specific schedule events
     *
     * @param event
     * @private
     */
    //todo: Do we actually need to use the original event?  The JQuery event has the required props
    // EX: event.originalEvent.touches[0].pageX --> event.touches[0].pageX
    private handleTouchEvent(event: TouchEvent): void {
        switch (event.type.toLowerCase()) {
            case 'touchstart':
                event.preventDefault();

                this.mouseStartX = event.touches[0].pageX;
                this.mouseStartY = event.touches[0].pageY;
                //reset mouse moves to original
                this.mouseMoveX = event.touches[0].pageX;
                this.mouseMoveY = event.touches[0].pageY;

                break;
            case 'touchmove':
                event.preventDefault();
                this.mouseMoveX = event.changedTouches[0].pageX;
                this.mouseMoveY = event.changedTouches[0].pageY;

                break;
            case 'touchend':
                event.preventDefault();
                this.mouseEndX = this.mouseMoveX;
                this.mouseEndY = this.mouseMoveY;

                if (
                    event.currentTarget &&
                    Math.abs(this.mouseStartX - this.mouseEndX) < this.touchTolerance &&
                    Math.abs(this.mouseStartY - this.mouseEndY) < this.touchTolerance
                ) {
                    const infoId = $(event.currentTarget).attr('data-info-id');
                    const rinkId = $(event.currentTarget).attr('data-rink-id');
                    if (infoId && rinkId) {
                        const eventClicked = this.getEventInfo(infoId, rinkId);
                        if (eventClicked) {
                            this.displaySingleEvent(eventClicked);
                        }
                    }
                }

                break;
        }
    }

    /**
     * Creates calendar and creates all its events in the date range
     */
    parseCalendars(
        calendars: CalendarWithEvents[],
        startDate: string,
        endDate: string,
        additionalDays: number | Nullish
    ): ScheduleInfo[] {
        //for additional days
        //collect all the events from all the calendars for each day
        const scheduleDays = additionalDays;
        const parsedCalendars: ScheduleInfo[] = []; //stores final list of events

        //update change_key
        calendars.forEach((calendar) => {
            const calendarIndex = this.calendarChangeKeyRecords.findIndex((item) => {
                return calendar.calendar_id === item.calendar_id;
            });

            //if calendar is found update key or else push the new calendar into the
            //calendars array
            if (calendarIndex !== -1) {
                this.calendarChangeKeyRecords[calendarIndex].change_key = calendar.change_key;
            } else {
                this.calendarChangeKeyRecords.push({
                    calendar_id: calendar.calendar_id,
                    change_key: calendar.change_key,
                });
            }
        });

        if (scheduleDays) {
            //If all calendar types are rink, then remain true, else gets set to
            //false.  Fix for previous hardcode -> rinktype: "Time-Event-MultiDay",
            const useRinkTypeRink = calendars.every(function (calendar) {
                return (
                    calendar.template &&
                    calendar.template.template_category &&
                    calendar.template.template_category.toLowerCase() === 'rink'
                );
            });

            //Get parsed events for all the calendars so that they can be reused
            const parsedEvents = calendars.map((calendar) => {
                return this.parseEvents(calendar, startDate, endDate);
            });

            //Get start and end date value for a day - this will be incremented by a
            //day after each loop
            const tempStartDate = moment(startDate);
            const tempEndDate = moment(startDate).hour(23).minute(59).second(59);

            for (let i = 0; i < scheduleDays; i++) {
                //get all the events from all the calendars for the day
                const events: ScheduleEventInfo[] = [];
                calendars.forEach((calendarItem, index) => {
                    const parsedEvent = parsedEvents[index];
                    const parsedEventRange = this.getEvents(
                        parsedEvent,
                        tempStartDate,
                        tempEndDate
                    );
                    const transformedEvents = this.transformEvents(
                        parsedEventRange,
                        calendarItem.template
                    );
                    events.push(...transformedEvents);
                });

                //Add the events to the list
                parsedCalendars.push({
                    headerinfo: {
                        date: tempStartDate.format('LLL'),
                        rinkname: `${tempStartDate.format('dddd')} (${tempStartDate.format(
                            'MMM'
                        )} ${tempStartDate.date()})`,
                        rinkname_date_info: {
                            date: tempStartDate.date(),
                            day_of_week: tempStartDate.format('dddd'),
                            month_name: tempStartDate.format('MMMM'),
                            month_number: tempStartDate.month() + 1,
                            number_day: tempStartDate.day(),
                            short_month_name: tempStartDate.format('MMM'),
                        },
                        TZOFFSET: null,
                        autoupload: 0,
                        collapse: 1,
                        // color1: calendar.color,
                        color2: null,
                        fontsize: 2,
                        importformat: null,
                        // mainheadertext: calendar.name,
                        numrows: 100, //calendar.show_rows || 4, // 4 is the default number of rows to show
                        // rinkname: calendar.name,
                        rinktype: useRinkTypeRink ? 'Rink' : 'Time-Event-MultiDay', //Defaults to "Time-Event-MultiDay" if !calendar.template || !calendar.template.template_category
                        screenformat: 'RINK',
                        screenid: this.config.screenid,
                        screenrefresh: 300,
                        scrollinfo: 1,
                        scrollspeed: this.config.scroll_speed,
                        showLocker: 1,
                        showorder: 0,
                    },
                    events: events,
                });

                //todo: Try to not mutate Moment
                tempStartDate.add('day', 1).hour(23).minute(59).second(59);
                tempEndDate.add('day', 1);
            }
            return parsedCalendars;
        } else {
            return calendars.map((calendar) => {
                const parsedEvent = this.parseEvents(calendar, startDate, endDate);
                const parsedEventRange = this.getEvents(parsedEvent, startDate, endDate);
                const transformedEvents = this.transformEvents(parsedEventRange, calendar.template);

                return {
                    headerinfo: {
                        TZOFFSET: null,
                        autoupload: 0,
                        calendar_id: calendar.calendar_id,
                        collapse: 0,
                        color1: calendar.color,
                        color2: null,
                        fontsize: 2,
                        importformat: null,
                        mainheadertext: calendar.name,
                        numrows: this.config.show_rows || calendar.show_rows || 4, // 4 is the default number of rows to show
                        rinkid: calendar.calendar_id,
                        rinkname: calendar.name,
                        rinktype: calendar.template.template_category || 'Time-Event-Location',
                        screenformat: 'RINK',
                        screenid: this.config.screenid,
                        screenrefresh: 300,
                        scrollinfo: 1,
                        scrollspeed: this.config.scroll_speed,
                        showLocker: 1,
                        showorder: 0,
                    },
                    events: transformedEvents,
                };
            });
        }
    }

    /**
     * Creates events for the calendar
     * @param calendar
     * @param startDate (format: 'YYYY-MM-DD HH:mm:ss')
     * @param endDate (format: 'YYYY-MM-DD HH:mm:ss')
     */
    parseEvents(calendar: CalendarWithEvents, startDate: string, endDate: string): ParsedEvents {
        const parsedEvents: ParsedEvents = {};
        calendar.events.forEach((event) => {
            const calendarEvent = this.createCalendarEvent(
                event,
                this.getTimezone(calendar),
                startDate,
                endDate
            );

            let startYear: number;
            let startDay: number;
            let startMonth: number;
            let endYear: number;
            let endDay: number;
            let endMonth: number;

            try {
                startYear = calendarEvent.startDateMoment.year();
                startDay = calendarEvent.startDateMoment.date();
                startMonth = calendarEvent.startDateMoment.month() + 1;
                endYear = calendarEvent.endDateMoment.year();
                endDay = calendarEvent.endDateMoment.date();
                endMonth = calendarEvent.endDateMoment.month() + 1;
            } catch (err) {
                Log.Error('Invalid event datetime', null, __fileline, err);
                // return to skip adding event with invalid date to parsedEvents
                return;
            }

            //Add calendarEvent to the events list if it is not recurring event.
            if (!calendarEvent.rrule) {
                //Check if property exists
                if (!objectHasOwnProperty(parsedEvents, `year${startYear}`)) {
                    parsedEvents[`year${startYear}`] = {};
                }
                if (!objectHasOwnProperty(parsedEvents[`year${startYear}`], `month${startMonth}`)) {
                    parsedEvents[`year${startYear}`][`month${startMonth}`] = {};
                }
                if (
                    !objectHasOwnProperty(
                        parsedEvents[`year${startYear}`][`month${startMonth}`],
                        `day${startDay}`
                    )
                ) {
                    parsedEvents[`year${startYear}`][`month${startMonth}`][`day${startDay}`] = [];
                }

                parsedEvents[`year${startYear}`][`month${startMonth}`][`day${startDay}`].push(
                    calendarEvent
                );

                //check if the event begins and ends on the same day, if not add it
                if (startYear !== endYear || startMonth !== endMonth || startDay !== endDay) {
                    //if enddate is smaller than startdate don't add it
                    //do while startdate is not equal to enddate
                    const eventDate = moment(calendarEvent.startDateMoment);

                    //loop while the date difference is more than 0 days
                    while (Math.floor(calendarEvent.endDateMoment.diff(eventDate, 'days')) > 0) {
                        eventDate.add(1, 'd');
                        const endYearTemp = eventDate.year();
                        const endDayTemp = eventDate.date();
                        const endMonthTemp = eventDate.month() + 1;

                        //Check if property exists
                        if (!objectHasOwnProperty(parsedEvents, `year${endYearTemp}`)) {
                            parsedEvents[`year${endYearTemp}`] = {};
                        }
                        if (
                            !objectHasOwnProperty(
                                parsedEvents[`year${endYearTemp}`],
                                `month${endMonthTemp}`
                            )
                        ) {
                            parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`] = {};
                        }
                        if (
                            !objectHasOwnProperty(
                                parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`],
                                `day${endDayTemp}`
                            )
                        ) {
                            parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`][
                                `day${endDayTemp}`
                            ] = [];
                        }

                        const duplicateEvent = { ...calendarEvent };
                        duplicateEvent.duplicateEvent = true;
                        duplicateEvent.parentEvent = calendarEvent;
                        parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`][
                            `day${endDayTemp}`
                        ].push(duplicateEvent);
                    }
                }
            }

            //Check if there are recurrences add recurrences to parsedEvents set
            if (calendarEvent.recurrences) {
                calendarEvent.recurrences.forEach((newRecurrence) => {
                    newRecurrence.parentEvent = calendarEvent; //reference to the parent event
                    const rstartYear = newRecurrence.startDateMoment.year();
                    const rstartDay = newRecurrence.startDateMoment.date();
                    const rstartMonth = newRecurrence.startDateMoment.month() + 1;
                    const rendYear = newRecurrence.endDateMoment.year();
                    const rendDay = newRecurrence.endDateMoment.date();
                    const rendMonth = newRecurrence.endDateMoment.month() + 1;

                    //Check if property exists
                    if (!objectHasOwnProperty(parsedEvents, `year${rstartYear}`)) {
                        parsedEvents[`year${rstartYear}`] = {};
                    }
                    if (
                        !objectHasOwnProperty(
                            parsedEvents[`year${rstartYear}`],
                            `month${rstartMonth}`
                        )
                    ) {
                        parsedEvents[`year${rstartYear}`][`month${rstartMonth}`] = {};
                    }
                    if (
                        !objectHasOwnProperty(
                            parsedEvents[`year${rstartYear}`][`month${rstartMonth}`],
                            `day${rstartDay}`
                        )
                    ) {
                        parsedEvents[`year${rstartYear}`][`month${rstartMonth}`][
                            `day${rstartDay}`
                        ] = [];
                    }

                    parsedEvents[`year${rstartYear}`][`month${rstartMonth}`][
                        `day${rstartDay}`
                    ].push(newRecurrence);

                    //check if the event begins and ends on the same day, if not add
                    //it
                    if (
                        rstartYear != rendYear ||
                        rstartMonth != rendMonth ||
                        rstartDay != rendDay
                    ) {
                        const eventDate = moment(newRecurrence.startDateMoment);

                        //loop while the date difference is more than 0 days
                        while (
                            Math.floor(newRecurrence.endDateMoment.diff(eventDate, 'days')) > 0
                        ) {
                            eventDate.add(1, 'd');
                            const endYearTemp = eventDate.year();
                            const endDayTemp = eventDate.date();
                            const endMonthTemp = eventDate.month() + 1;

                            //Check if property exists
                            if (!objectHasOwnProperty(parsedEvents, `year${endYearTemp}`)) {
                                parsedEvents[`year${endYearTemp}`] = {};
                            }
                            if (
                                !objectHasOwnProperty(
                                    parsedEvents[`year${endYearTemp}`],
                                    `month${endMonthTemp}`
                                )
                            ) {
                                parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`] = {};
                            }
                            if (
                                !objectHasOwnProperty(
                                    parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`],
                                    `day${endDayTemp}`
                                )
                            ) {
                                parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`][
                                    `day${endDayTemp}`
                                ] = [];
                            }

                            //This is not a deep clone.  Can we use Object.assign?
                            const duplicateEvent: RecurrenceEvent = jQuery.extend(
                                {
                                    duplicateEvent: true,
                                    parentEvent: newRecurrence,
                                },
                                newRecurrence
                            );
                            parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`][
                                `day${endDayTemp}`
                            ].push(duplicateEvent);
                        }
                    }
                });
            }

            //Check if there are exceptions add exceptions to parsedEvents set
            if (calendarEvent.exceptions) {
                calendarEvent.exceptions.forEach((exception) => {
                    const estartYear = exception.startDateMoment.year();
                    const estartDay = exception.startDateMoment.date();
                    const estartMonth = exception.startDateMoment.month() + 1;
                    const eendYear = exception.endDateMoment.year();
                    const eendDay = exception.endDateMoment.date();
                    const eendMonth = exception.endDateMoment.month() + 1;

                    //Check if property exists
                    if (!objectHasOwnProperty(parsedEvents, `year${estartYear}`)) {
                        parsedEvents[`year${estartYear}`] = {};
                    }
                    if (
                        !objectHasOwnProperty(
                            parsedEvents[`year${estartYear}`],
                            `month${estartMonth}`
                        )
                    ) {
                        parsedEvents[`year${estartYear}`][`month${estartMonth}`] = {};
                    }
                    if (
                        !objectHasOwnProperty(
                            parsedEvents[`year${estartYear}`][`month${estartMonth}`],
                            `day${estartDay}`
                        )
                    ) {
                        parsedEvents[`year${estartYear}`][`month${estartMonth}`][
                            `day${estartDay}`
                        ] = [];
                    }

                    parsedEvents[`year${estartYear}`][`month${estartMonth}`][
                        `day${estartDay}`
                    ].push(exception);

                    //check if the event begins and ends on the same day, if not add
                    //it
                    if (
                        estartYear != eendYear ||
                        estartMonth != eendMonth ||
                        estartDay != eendDay
                    ) {
                        const eventDate = moment(exception.startDateMoment);

                        //loop while the date difference is more than 0 days
                        while (Math.floor(exception.endDateMoment.diff(eventDate, 'days')) > 0) {
                            eventDate.add(1, 'd');
                            const endYearTemp = eventDate.year();
                            const endDayTemp = eventDate.date();
                            const endMonthTemp = eventDate.month() + 1;

                            //Check if property exists
                            if (!objectHasOwnProperty(parsedEvents, `year${endYearTemp}`)) {
                                parsedEvents[`year${endYearTemp}`] = {};
                            }
                            if (
                                !objectHasOwnProperty(
                                    parsedEvents[`year${endYearTemp}`],
                                    `month${endMonthTemp}`
                                )
                            ) {
                                parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`] = {};
                            }
                            if (
                                !objectHasOwnProperty(
                                    parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`],
                                    `day${endDayTemp}`
                                )
                            ) {
                                parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`][
                                    `day${endDayTemp}`
                                ] = [];
                            }

                            //This is not a deep clone.  Can we use Object.assign?
                            const duplicateEvent = jQuery.extend(
                                {
                                    duplicateEvent: true,
                                    parentEvent: exception,
                                },
                                exception
                            );
                            parsedEvents[`year${endYearTemp}`][`month${endMonthTemp}`][
                                `day${endDayTemp}`
                            ].push(duplicateEvent);
                        }
                    }
                });
            }
        });

        return parsedEvents;
    }

    /**
     * Transform calendar metadata properties to predefined schedule properties
     */
    transformEvents(
        events: ScheduleWidgetDisplayEvent[],
        template: CalendarTemplate
    ): ScheduleEventInfo[] {
        return events
            .map((event) => {
                const obj: ScheduleEventInfo = {
                    infoid: event.eventId, //for backwards compatibility
                    information: event.metadata.information || event.metadata.Name,
                    sdate: event.startDateMoment.format('ll'),
                    stime: event.startDateMoment.format('h:mm a'),
                    edate: event.endDateMoment.format('ll'), //"11:00",
                    etime: event.endDateMoment.format('h:mm a'),
                    location: event.metadata.location || '', //for backwards compatibility
                    instructor: event.metadata.instructor || '', //for backwards compatibility
                    descr: event.metadata.Description
                        ? event.metadata.Description.replace(/(\r\n|\n\r|\r|\n)/g, '<br>')
                        : '', //for backwards compatibility
                    event: event.eventId,
                    hometeam: event.metadata.hometeam || null, //for backwards compatibility
                    awayteam: event.metadata.awayteam || null, //for backwards compatibility
                    awaylocker: event.metadata.awaylocker || null, //for backwards compatibility
                    homelocker: event.metadata.homelocker || null, //for backwards compatibility
                    rinkname: event.metadata.rinkname || '', //for backwards compatibility
                    rinkid: (event as ScheduleWidgetEvent).rinkid || '', //for backwards compatibility
                    mainheadertext: event.metadata.mainheadertext || '', //for backwards compatibility
                    allday: (event as ScheduleWidgetEvent).allDay === true ? 'true' : 'false', //Converting it to string for backwards compatibility
                    endtime: event.endDateMoment.format('h:mm a'),
                    facilityid: this.config.facilityid,
                    rinktype: template.template_category,
                    starttime: event.startDateMoment.format('h:mm a'), //moment-format-LT //ref-https://momentjs.com/docs/#/displaying/
                    startDateMoment: event.startDateMoment,
                    endDateMoment: event.endDateMoment,
                    metadata: {},
                };

                if (
                    event.startDateMoment.format('h:mm A') === '12:00 AM' &&
                    event.endDateMoment.format('h:mm A') === '11:59 PM'
                ) {
                    obj.allday = 'true';
                }

                //Assign Metadata
                template.fields.forEach(function (field) {
                    obj[field.signage_property_name] =
                        event.metadata[field.name] || obj[field.signage_property_name];
                });

                //Assign All Metadata
                for (const prop in event.metadata) {
                    obj.metadata[prop] = event.metadata[prop];
                }

                return obj;
            })
            .filter((event) => {
                //if event is allday event then it need not be filtered
                if (event.allday === 'true') {
                    return true;
                }
                // if show expired data is true show all events that falls today
                if (this.config.show_expired_data === true) {
                    return event.endDateMoment > moment().hour(0).minute(0).second(0);
                }
                return event.endDateMoment > moment();
            })
            .sort(function (a, b) {
                if (a.startDateMoment < b.startDateMoment) {
                    return -1;
                }
                if (a.startDateMoment > b.startDateMoment) {
                    return 1;
                }
                return 0;
            });
    }

    /**
     * Returns an instance of calendar event with its recurrences and events
     * @param event
     * @param localTimezone
     * @param startDateRange (format: 'YYYY-MM-DD HH:mm:ss')
     * @param endDateRange (format: 'YYYY-MM-DD HH:mm:ss')
     */
    createCalendarEvent(
        event: RawEvent,
        localTimezone: string,
        startDateRange: string,
        endDateRange: string
    ): ScheduleWidgetEvent {
        let startDateMoment: Moment;
        let endDateMoment: Moment;
        let startDate: string;
        let endDate: string;

        if (event.all_day) {
            startDateMoment = moment.tz(event.start_date, 'utc');
            startDate = moment.tz(event.start_date, 'utc').format();
            endDateMoment = moment.tz(event.end_date, 'utc').hour(23).minute(59).second(59);
            endDate = moment.tz(event.end_date, 'utc').hour(23).minute(59).second(59).format();
        } else {
            startDate = this.getTimeFromTimestamp(event.start_date, localTimezone);
            startDateMoment = this.getMoment(event.start_date, localTimezone);
            endDate = this.getTimeFromTimestamp(event.end_date, localTimezone);
            endDateMoment = this.getMoment(event.end_date, localTimezone);
        }

        const calendarEventObject: ScheduleWidgetEvent = {
            startDateMoment: startDateMoment,
            startDate: startDate,
            endDateMoment: endDateMoment,
            endDate: endDate,
            localTimezone: localTimezone,
            calendarId: event.calendar_id,
            eventId: event.event_id,
            eventFirstOccurrence: this.getTimeFromTimestamp(
                event.event_first_occurrence,
                localTimezone
            ),
            eventLastOccurence: isNullish(event.event_last_occurrence)
                ? null
                : this.getTimeFromTimestamp(event.event_last_occurrence, localTimezone),
            rinkid: event.calendar_id,
            rrule: event.rrule || null,
            metadata: event.metadata || null,
            exceptions: event.exceptions ? this.addExceptions(event.exceptions) : null,
            rruleset: null,
            allDay: event.all_day || false,
        };

        if (event.rrule) {
            calendarEventObject.recurrences = [];
            this.extrapolate(calendarEventObject, startDateRange, endDateRange, localTimezone);
        }

        return calendarEventObject;
    }

    /**
     * Creates exception events
     */
    addExceptions(exceptions: RawExceptionEvent[]): ScheduleExceptionEvent[] {
        let idx = 0;
        return exceptions.map((exception) => {
            return this.createExceptionEvent(
                {
                    eventRecurs: false, //was previously undefined
                    eventRecurrenceId: exception.event_recurrence_id,
                    calendarId: exception.calendar_id,
                    eventId: exception.event_id,
                    rrule: exception.rrule || null,
                    startDate: exception.start_date,
                    endDate: exception.end_date, // add eventLength to find endDate
                    metadata: exception.metadata,
                    allDay: exception.all_day,
                },
                undefined,
                `exception_${++idx}`
            );
        });
    }

    /**
     * Create events based on rrule
     * @param calendarEventObject
     * @param startDate (format: 'YYYY-MM-DD HH:mm:ss')
     * @param endDate (format: 'YYYY-MM-DD HH:mm:ss')
     * @param localTimezone
     */
    extrapolate(
        calendarEventObject: ScheduleWidgetEvent,
        startDate: string,
        endDate: string,
        localTimezone: string
    ): void {
        let quarterStartDate;
        let quarterEndDate;
        const startYear = parseInt(moment.tz(startDate, localTimezone).format('YYYY'));
        const endYear = parseInt(moment.tz(endDate, localTimezone).format('YYYY'));

        //calculate eventLength to find endDate for events
        const eventLength = moment(calendarEventObject.endDate).diff(
            moment(calendarEventObject.startDate)
        );

        if (!calendarEventObject.rrule) {
            return;
        }

        /*
         * we are create two copies of rruleset because the rruleset in variable
         * rule will be modified, but we need to keep original copy or rruleset
         * as well which is in calendarEventObject.rruleset
         *
         * @todo: Value is never read again, do we actually need to store it?
         */
        calendarEventObject.rruleset = getRRuleSetFromString(calendarEventObject.rrule);
        const rule = getRRuleSetFromString(calendarEventObject.rrule);

        //Get start and end date that spans two quarters
        switch (this.getQuarter(endDate)) {
            case CalendarQuarter.FIRST_QUARTER:
                quarterStartDate = moment(`${startYear - 1}-10-01`).toDate();
                quarterEndDate = moment(`${endYear}-03-31`).toDate();
                break;
            case CalendarQuarter.SECOND_QUARTER:
                quarterStartDate = moment(`${startYear}-01-01 00:00:00`).toDate();
                quarterEndDate = moment(`${endYear}-06-30 00:00:00`).toDate();
                break;
            case CalendarQuarter.THIRD_QUARTER:
                quarterStartDate = moment(`${startYear}-04-01 00:00:00`).toDate();
                quarterEndDate = moment(`${endYear}-09-30 00:00:00`).toDate();
                break;
            case CalendarQuarter.FOURTH_QUARTER:
                quarterStartDate = moment(`${startYear}-07-01 00:00:00`).toDate();
                quarterEndDate = moment(`${endYear}-12-31 00:00:00`).toDate();
                break;
        }

        //We need to calculate this if timezone offset is negative
        let doesDtstartFallOnNextDay = false;

        //We need to calculate this if timezone offset is positive
        let doesDtstartFallOnPreviousDay = false;

        if (moment().utcOffset() <= 0) {
            /*
             * rrule.between does not work as expected if the event dtstart is
             * in next day in GMT. The events generated show up in previous day.
             * To get around this we calculate the offset and if it falls on
             * next day in GMT we add a day to the recurrences.
             */
            if (!calendarEventObject.allDay) {
                doesDtstartFallOnNextDay =
                    (24 - moment(rule._rrule[0].options.dtstart).get('hour')) * 60 +
                        moment(rule._rrule[0].options.dtstart).utcOffset() <=
                    0;

                if (doesDtstartFallOnNextDay) {
                    /**
                     * Reduce a day from dtstart if its value is next day in
                     * GMT. The first occurrence is not generated if we do not
                     * reduce a day.
                     */
                    rule._rrule[0].options.dtstart = new Date(
                        +rule._rrule[0].options.dtstart - 24 * 60 * 60 * 1000
                    );

                    //Vendor type defs set this property as readonly,
                    // so we must override to writeable type
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                    (rule._exdate as Date[]) = rule._exdate.map(function (date) {
                        return new Date(+date - 24 * 60 * 60 * 1000);
                    });
                }
            }
        } else {
            if (!calendarEventObject.allDay) {
                /*
                 * rrule.between does not work as expected if the event dtstart
                 * is in previous day in GMT. The events generated show up next
                 * day. To get around this we calculate the offset and if it
                 * falls previous day in GMT we reduce a day to the recurrences.
                 */
                doesDtstartFallOnPreviousDay =
                    moment(rule._rrule[0].options.dtstart).utcOffset() -
                        moment(rule._rrule[0].options.dtstart).get('hour') * 60 >=
                    0;

                if (doesDtstartFallOnPreviousDay) {
                    //Vendor type defs set this property as readonly,
                    // so we must override to writeable type
                    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
                    (rule._exdate as Date[]) = rule._exdate.map(function (date) {
                        return new Date(+date + 24 * 60 * 60 * 1000);
                    });
                }
            }
        }

        const eventDates = rule
            .between(quarterStartDate, quarterEndDate, true)
            .map(function (date) {
                /*
                 * Add a day
                 * +date converts date object to timestamp
                 */
                if (doesDtstartFallOnNextDay) {
                    return new Date(+date + 24 * 60 * 60 * 1000);
                }
                if (doesDtstartFallOnPreviousDay) {
                    return new Date(+date - 24 * 60 * 60 * 1000);
                }
                return date;
            });

        //Create list of recurrences
        const firstDateIsDST = moment(calendarEventObject.startDate).isDST();
        eventDates.forEach((eventDate: Date, index: number) => {
            const eventMoment = moment(eventDate).tz(localTimezone);
            if (!calendarEventObject.allDay) {
                const eventMomentIsDST = eventMoment.isDST();
                if (firstDateIsDST !== eventMomentIsDST) {
                    eventMomentIsDST
                        ? eventMoment.subtract(1, 'hours')
                        : eventMoment.add(1, 'hours');
                }
            }

            const ex = this.createExceptionEvent(
                {
                    eventRecurrenceId: null,
                    eventRecurs: true,
                    calendarId: calendarEventObject.calendarId,
                    eventId: calendarEventObject.eventId,
                    rrule: calendarEventObject.rrule,
                    startDate: eventMoment.unix() * 1000,
                    endDate: eventMoment.unix() * 1000 + eventLength, // add eventLength to find endDate
                    metadata: calendarEventObject.metadata,
                    allDay: calendarEventObject.allDay,
                },
                calendarEventObject.localTimezone,
                String(index + 1)
            );
            calendarEventObject.recurrences?.push(ex as RecurrenceEvent);
        });
    }

    /**
     * Creates exception object
     */
    createExceptionEvent(
        exevent: newExceptionEvent,
        timezone: string | undefined = undefined,
        index: string //TODO: the string format is wildly inconsistent between calls
    ): ScheduleExceptionEvent {
        const resultTimezone = exevent.allDay ? 'UTC' : timezone || this.getTimezone();
        let startDateMoment;
        let endDateMoment;

        /**
         * These values are used to filter data so time is modified to 23.59.59.
         */
        if (exevent.allDay) {
            startDateMoment = this.getMoment(exevent.startDate, resultTimezone);
            endDateMoment = this.getMoment(exevent.endDate, resultTimezone)
                ?.hour(23)
                .minute(59)
                .second(59);
        } else {
            startDateMoment = this.getMoment(exevent.startDate, resultTimezone);
            endDateMoment = this.getMoment(exevent.endDate, resultTimezone);
        }
        return {
            eventRecurrenceId: exevent.eventRecurrenceId,
            eventRecurs: exevent.eventRecurs,
            calendarId: exevent.calendarId,
            eventId: exevent.eventId,
            eventEffectiveId: exevent.eventId + '[' + index + ']',
            rrule: exevent.rrule || null,
            startDate: this.getTimeFromTimestamp(exevent.startDate, resultTimezone),
            endDate: this.getTimeFromTimestamp(exevent.endDate, resultTimezone),
            startDateMoment: startDateMoment,
            endDateMoment: endDateMoment,
            metadata: exevent.metadata,
        };
    }

    /**
     * Get events that fall within the start and end date range
     * @param events
     * @param startDate (format: 'YYYY-MM-DD HH:mm:ss')
     * @param endDate (format: 'YYYY-MM-DD HH:mm:ss')
     */
    getEvents(
        events: ParsedEvents,
        startDate: string | Moment,
        endDate: string | Moment
    ): ScheduleWidgetDisplayEvent[] {
        const returnList: ScheduleWidgetDisplayEvent[] = [];
        const startDateMoment = moment(startDate).tz(this.getTimezone());
        const endDateMoment = moment(endDate).tz(this.getTimezone());

        while (startDateMoment <= endDateMoment) {
            const day = startDateMoment.date();
            const month = startDateMoment.month() + 1;
            const year = startDateMoment.year();
            let result: ScheduleWidgetDisplayEvent[] = [];
            let eventList: ScheduleWidgetDisplayEvent[] = [];

            if (events) {
                if (!day || !month || !year) {
                    //Previously, there was code to parse events if not all these values were set
                    //It's unlikely that the code was ever actually run, so it was removed
                    throw new Error('Day, month, and year must be set for every calendar event');
                }

                if (
                    events[`year${year}`] &&
                    events[`year${year}`][`month${month}`] &&
                    events[`year${year}`][`month${month}`][`day${day}`]
                ) {
                    result = events[`year${year}`][`month${month}`][`day${day}`];
                }
            }

            //If the getEvent is for a month or year remove duplicate events.
            if (!day) {
                //Previously, there was code to parse events if not all these values were set
                //It's unlikely that the code was ever actually run, so it was removed
                throw new Error('Day must be set for every calendar event');
            }

            eventList = result;

            returnList.push(...eventList);

            //increment the day by 1
            startDateMoment.add(1, 'd');
        }

        return returnList;
    }

    /**
     * Creates datetime string from unix timestamp
     */
    getTimeFromTimestamp(datetime: number, timezone: string): string {
        return this.getMoment(datetime, timezone).format();
    }

    /**
     * Gets moment object from unix timestamp
     */
    getMoment(unixTimestamp: number, timezone: string): Moment {
        if (!isNaN(unixTimestamp)) {
            return moment(unixTimestamp).tz(timezone);
        }
        const errorMessage = `Unable to convert Unix timestamp to moment`;

        Log.Error(errorMessage, null, __fileline, {
            timestamp: unixTimestamp,
            timezone: timezone,
        });

        throw new Error(errorMessage);
    }

    /**
     * Gets quarter from datetime
     */
    getQuarter(datetime: string): CalendarQuarter {
        const validQuarters: CalendarQuarter[] = [1, 2, 3, 4];
        const quarter = parseInt(moment(datetime).tz('UTC').format('Q'));

        if (!validQuarters.includes(quarter)) {
            throw new Error(`Unable to parse quarter from ${datetime}`);
        }

        return quarter;
    }

    getTimezone(calendar?: Calendar): string {
        if (!this.config.local_timezone && calendar && moment.tz.zone(calendar.local_timezone)) {
            return calendar.local_timezone;
        } else if (moment.tz.zone(Signage.localTimeZone)) {
            return Signage.localTimeZone;
        }

        const errorMsg = `No known timezone matches found for calendar widget ${this.id}`;

        Log.Error(errorMsg, null, __fileline, {
            useLocalTimeZone: this.config.local_timezone,
            signageConfig: Signage.localTimeZone,
            calendarConfig: calendar?.local_timezone,
        });

        throw new Error(errorMsg);
    }

    private subscribeToCalendars(calendarIds: string[]): void {
        calendarIds.forEach((calendarId) => {
            pushReceiver()
                .subscribeToCalendar(calendarId)
                .then(() => {
                    Log.Info(
                        `Schedule Widget Subscribed. widgetsid=${this.id}, calendar_id=${calendarId}`,
                        null,
                        __fileline
                    );
                })
                .catch((error: unknown) => {
                    Log.Error(
                        `Failed to subscribe to calendar channel widgetsid=${this.id}, calendar_id=${calendarId}`,
                        null,
                        __fileline,
                        error
                    );
                });
        });
    }
}

/**
 * This function is run every time the widget is played, and every minute
 * afterwards
 *
 * @todo: refactor 'this' param, and basically all of schedulerIntervalAction
 */
function scheduleIntervalAction(this: Widget & ScheduleWidget) {
    if (this.isPaused) {
        return;
    }

    let totalLength = 0;
    let newLength = 0;

    this.schedules.forEach((schedule) => {
        totalLength += schedule.events.length;
        schedule.events = schedule.events.filter((item) => {
            //if item is allday event then it need not be filtered
            if (item.allday === 'true') {
                return true;
            }
            // if show expired data is true show all events that falls today
            if (this.config.show_expired_data === true) {
                return item.endDateMoment > moment().hour(0).minute(0).second(0);
            }
            return item.endDateMoment > moment();
        });
        newLength += schedule.events.length;
    });

    if (totalLength !== newLength) {
        this.hasContentToPlay = newLength !== 0;

        /**
         * We update the this.schedulePromiseData cached value because
         * there's no need to render the filtered events anymore
         * If the cache is not updated the past events display for a
         * brief moment when the widget is re-rendered from STOPPED state
         */
        this.schedulePromiseData = this.schedules;
        this.renderContentWithGlobals()
            .catch(() => {
                Log.Error(`Failed to render widget content (${this.id}:${this.name})`);
            })
            .then(() => {
                //UI touch and marquee setup

                this.setupTouchUI();
            })
            .catch((error) => {
                Log.Warning(
                    `Failed to get Schedule for Widget:${this.id}`,
                    null,
                    __fileline,
                    error
                );
            });
    }
}

export { ScheduleWidget };
